import React from 'react';
import { Link } from 'react-router-dom';
import { Monitor, ChevronRight, Clock4, Flame } from 'lucide-react';
import { getImageUrl, CATEGORY_LABELS } from '../config';
import ComingSoonOverlay from './ComingSoonOverlay';

const STATUS_BADGES = {
  available: { label: 'Disponible', cls: 'bg-emerald-500/90 text-white border-white/20' },
  reserved: { label: 'Réservé', cls: 'bg-slate-700/90 text-white border-white/20' },
  sold: { label: 'Vendu', cls: 'bg-slate-900/90 text-white border-white/20' },
};

const CONDITION_BADGES = {
  reconditionne: { label: 'Reconditionné', cls: 'bg-secondary/90 text-white border-white/20' },
  neuf: { label: 'Neuf', cls: 'bg-emerald-500/90 text-white border-white/20' },
};

export default function ProductCard({ product }) {
  const status = STATUS_BADGES[product.status] || STATUS_BADGES.available;
  const condition = CONDITION_BADGES[product.condition] || CONDITION_BADGES.reconditionne;
  const isSold = product.status === 'sold';
  const isReserved = product.status === 'reserved';
  const mainImage = product.images?.[0] ? getImageUrl(product.images[0]) : null;

  // Format brand line : "HP · Écran" / "HP · Imprimante" (vraie catégorie) ou,
  // pour les PC génériques (recondi category="pc"), détection Portable / PC fixe.
  const isLaptop = product.screen_size || /portable|laptop|notebook|probook|elitebook|latitude|inspiron|thinkpad/i.test(product.name || '');
  const specificCat = product.category && product.category !== 'pc' && CATEGORY_LABELS[product.category];
  const formatLabel = specificCat
    ? CATEGORY_LABELS[product.category]
    : (isLaptop
        ? `Portable${product.specs?.screen_size_inches ? ` ${product.specs.screen_size_inches}"` : ''}`
        : 'PC fixe');
  const brandLine = `${product.brand || 'PC'} · ${formatLabel}`;
  // Un produit DISPONIBLE est commandable et doit s'afficher comme un produit normal,
  // même neuf. Le traitement « bientôt en stock » (fond ardoise + overlay) ne concerne
  // plus que les neufs INDISPONIBLES (réservé / vendu).
  const isComingSoon = product.condition === 'neuf' && product.status !== 'available';

  return (
    <Link
      to={`/produit/${product.id}`}
      className={`group flex flex-col bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-secondary hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 ${isSold ? 'opacity-60' : ''}`}
      data-testid={`product-card-${product.id}`}
    >
      {/* Photo — aspect carré, object-contain pour ne pas crop, padding pour respirer.
          Neuf (en appro) = fond ardoise foncée (navy) pour que la carte se distingue des
          cartes reconditionné (fond clair) — le produit PNG transparent flotte en spotlight. */}
      <div
        className={`aspect-square relative flex items-center justify-center p-6 ${isComingSoon && !isSold ? 'product-photo-wrap--coming-soon' : 'bg-slate-100'}`}
        style={isSold ? { filter: 'grayscale(75%) brightness(0.9)' } : undefined}
      >
        {mainImage ? (
          <img
            src={mainImage}
            alt={product.name}
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <Monitor className="w-12 h-12 text-slate-300" />
        )}

        {/* Badges overlay top-left */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <span className={`font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border backdrop-blur-sm ${condition.cls}`}>
            {condition.label}
          </span>
          {product.grade && (
            <span className="font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-white/90 text-primary border border-primary/10 backdrop-blur-sm">
              Grade {product.grade}
            </span>
          )}
        </div>

        {/* Status badge top-right (uniquement si pas disponible — disponible est implicite).
            Caché pour reserved/sold qui ont leur propre overlay visuel fort. */}
        {product.status !== 'available' && !isReserved && !isSold && (
          <div className="absolute top-3 right-3">
            <span className={`font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border backdrop-blur-sm ${status.cls}`}>
              {status.label}
            </span>
          </div>
        )}

        {/* Deal badge bottom-left */}
        {product.has_active_deal && (
          <div className="absolute bottom-3 left-3">
            <span className="font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-secondary/90 text-white border border-white/20 backdrop-blur-sm">
              Deal en cours
            </span>
          </div>
        )}

        {/* Badge « Commande récente » (featured_label) — signal d'engouement positif posé
            automatiquement après une commande payée. Affiché sur un produit DISPONIBLE pour
            inciter d'autres acheteurs (amortir les frais de port). N'écrase pas un Deal. */}
        {product.featured_label && product.status === 'available' && !product.has_active_deal && (
          <div className="absolute bottom-3 left-3">
            <span className="font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-amber-500/90 text-white border border-white/20 backdrop-blur-sm inline-flex items-center gap-1">
              <Flame className="w-3 h-3" />
              {product.featured_label}
            </span>
          </div>
        )}

        {/* Bandeau RÉSERVÉ — déplacé en BAS de photo (boss 2026-05-29) pour
            ne plus cacher les puces Reconditionné / Grade A en top-left. */}
        {isReserved && (
          <div
            className="absolute bottom-0 left-0 right-0 bg-slate-800 text-white font-heading font-bold uppercase tracking-widest text-[11px] py-1.5 text-center shadow-md pointer-events-none flex items-center justify-center gap-1.5"
            data-testid="card-reserved-banner"
          >
            <Clock4 className="w-3 h-3" />
            Réservé
          </div>
        )}

        {/* Tampon VENDU diagonal style certificat (cohérent fiche détail) */}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="card-sold-stamp">
            <span
              className="font-heading font-bold uppercase tracking-[0.25em] text-2xl text-rose-700/90 border-[3px] border-rose-700/90 px-4 py-1 bg-white/85 backdrop-blur-[2px] rounded-sm shadow-lg"
              style={{ transform: 'rotate(-12deg)' }}
            >
              Vendu
            </span>
          </div>
        )}

        {/* Coming soon overlay : produits neufs en cours d'approvisionnement */}
        {isComingSoon && !isSold && <ComingSoonOverlay variant="card" />}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-4 gap-2">
        <span className="font-accent text-[11px] uppercase tracking-widest text-slate-500">{brandLine}</span>

        <h3 className="font-heading font-semibold uppercase text-sm leading-tight tracking-tight text-primary line-clamp-2 min-h-[2.5em]">
          {product.name}
        </h3>

        {/* Specs chips */}
        {(product.processor || product.ram || product.storage) && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {product.processor && (
              <span className="font-accent text-[10px] font-medium uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-sm">
                {product.processor.replace(/^Intel /, '').split(' ').slice(0, 3).join(' ')}
              </span>
            )}
            {product.ram && (
              <span className="font-accent text-[10px] font-medium uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-sm">
                {product.ram}
              </span>
            )}
            {product.storage && (
              <span className="font-accent text-[10px] font-medium uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-sm">
                {product.storage}
              </span>
            )}
          </div>
        )}

        {/* Badge réparabilité — affiché UNIQUEMENT si score moyen ≥ 7.
            Un score < 7 (ou non-noté) reste silencieux : un score moyen
            visible dévaloriserait la fiche au lieu de la mettre en valeur.
            Règle métier validée boss 2026-05-28 : "score non-validé est
            pire qu'un score absent". */}
        {product.repairability_status === 'scored' && product.repairability_scores && (
          (() => {
            const vals = Object.values(product.repairability_scores).filter(v => typeof v === 'number');
            if (vals.length === 0) return null;
            const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
            if (avg < 7) return null;  // Pas d'affichage sous le seuil
            return (
              <span className="font-accent text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border self-start text-emerald-700 bg-emerald-50 border-emerald-300">
                ♻️ Réparable {avg.toFixed(1)}/10
              </span>
            );
          })()
        )}

        {/* Footer : prix + CTA */}
        <div className="mt-auto pt-3 border-t border-slate-100 flex justify-between items-baseline gap-2">
          <span className="font-heading font-bold text-2xl text-secondary leading-none">
            {Math.floor(product.price || 0)}
            <span className="text-sm">,{((product.price || 0) % 1).toFixed(2).slice(2)} €</span>
          </span>
          <span className="font-heading text-xs font-semibold uppercase tracking-wider text-primary inline-flex items-center gap-0.5 group-hover:text-secondary transition-colors">
            {isComingSoon ? 'Bientôt en stock' : 'Voir la fiche'}
            <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}
